import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { filesize } from "src/ui/utils/filesize";
import { isUnlimited } from "src/ui/utils/limit";
import { makeStyles } from "tss-react/mui";

import { isLimitReached } from "@dashboard/workspaces/WorkspacesPage.utils";
import { Box, Theme, Typography } from "@mui/material";
import { CustomTileContainer, CustomTileTitle } from "@shared/CustomTile";
import { CheckboxField } from "@shared/form/CheckboxField";
import { Tooltip } from "@shared/Tooltip";

import { getFeature } from "./utils";

export const featureOn = 1;
const featureOff = 0;

const useStyles = makeStyles()((muiTheme: Theme) => ({
    tileContainer: {
        width: "100%",
        minWidth: 0,
        cursor: "pointer",
    },
    tileContainerStatic: {
        width: "100%",
        minWidth: 0,
    },
    contentBox: {
        padding: muiTheme.spacing(2),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 60,
    },
    flexBox: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    warningIcon: {
        marginLeft: muiTheme.spacing(1),
        color: muiTheme.palette.emteria.warning,
    },
    // The whole tile is the click target, so this is not a TextLink — but it carries the same affordance,
    // so it uses the same colours: link blue, warning orange on hover, never underlined.
    link: {
        color: muiTheme.palette.emteria.link,
        "&:hover": {
            color: muiTheme.palette.emteria.warning,
        },
    },
}));

type Props = {
    title: string;
    editable?: boolean;
    onClick?: () => void;
};
const BasicFeatureTile = (props: React.PropsWithChildren<Props>) => {
    const { classes } = useStyles();

    const containerClass = props.editable ? classes.tileContainer : classes.tileContainerStatic;

    return (
        <CustomTileContainer onClick={props.onClick} className={containerClass}>
            <CustomTileTitle>{props.title}</CustomTileTitle>
            <Box className={classes.contentBox}>{props.children}</Box>
        </CustomTileContainer>
    );
};

type AddonFeatureTileProps = {
    title: string;
    limitType: API.Feature["feature"];
    features: API.Feature[];
    editable: boolean;
    onToggle?: (limitType: string, newValue: number) => void;
};
export const AddonFeatureTile = (props: AddonFeatureTileProps) => {
    const { t } = useTranslation(["common"]);
    const { classes } = useStyles();

    const { used } = getFeature(props.limitType, props.features);
    const label = used ? t("common:limits.features.enabled") : t("common:limits.features.disabled");

    const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (props.onToggle && props.limitType) {
            const newValue = event.target.checked ? featureOn : featureOff;
            props.onToggle(props.limitType, newValue);
        }
    };

    return (
        <BasicFeatureTile title={props.title} editable={props.editable}>
            <Box className={classes.flexBox}>
                <CheckboxField checked={Boolean(used)} onChange={handleToggle} disabled={!props.editable} label={label} />
            </Box>
        </BasicFeatureTile>
    );
};

type LimitFeatureTileProps = {
    title: string;
    limitType: API.Feature["feature"];
    features: API.Feature[];
    editable: boolean;
    onClick?: (limitType: string, currentLimit: number) => void;
};
export const LimitFeatureTile = (props: LimitFeatureTileProps) => {
    const { t } = useTranslation(["common"]);
    const { classes } = useStyles();

    const { used = 0, max } = getFeature(props.limitType, props.features);
    const isReached = isLimitReached(used, max);
    const formattedValue = props.limitType === "FileSizeLimit" ? filesize(used) : used;

    const unlimited = isUnlimited(max, props.limitType);
    const label = unlimited ? `${formattedValue} / ${t("common:label.unlimited")}` : `${formattedValue} / ${max}`;
    const contentClass = props.editable ? clsx(classes.flexBox, classes.link) : classes.flexBox;

    const onClick = () => {
        if (props.onClick && props.limitType && props.editable) {
            props.onClick(props.limitType, max);
        }
    };

    return (
        <BasicFeatureTile title={props.title} onClick={onClick} editable={props.editable}>
            {/* A div, because it holds a Box and the reached-limit tooltip's wrapper, which a <p> may not contain */}
            <Typography variant="subtitle2" component="div" align="center">
                <Box className={contentClass}>
                    <Typography variant="subtitle2" data-testid={`limitFeatureTile.${props.limitType}`}>
                        {label}
                    </Typography>
                    {isReached && !unlimited ? (
                        <Tooltip title={t("common:limits.tooltip.limitHasBeenReached")}>
                            <i className={clsx("fa-solid fa-triangle-exclamation", classes.warningIcon)} />
                        </Tooltip>
                    ) : null}
                </Box>
            </Typography>
        </BasicFeatureTile>
    );
};
